<template>
  <Editor
    ref="editorRef"
    :key="editorRenderKey"
    v-model="model"
    :spellcheck="spellcheckActive"
    class="editor-styles mce-content-body min-h-[90px] flex-grow overflow-y-auto border-2 border-transparent p-2 pt-1 outline-none focus:border-lexmea-selection-darker focus:outline-none"
    :class="computedClass"
    :disabled="disabled"
    plugins="advlist lists nonbreaking table link visualchars"
    :toolbar="isSmallerThanSm ? mobileToolbox : desktopToolbox"
    tinymceScriptSrc="/tinymce/tinymce.min.js"
    licenseKey="gpl"
    :inline="true"
    :init="editorInit(onInit)"
    @click="handleClick"
  />
</template>

<script lang="ts" setup>
import Editor from "@tinymce/tinymce-vue";
import { editorInit } from "./editorInit";
import { useScrollTinymceTocHeadingToView } from "./useScrollTinymceTocHeadingToView";
import { useInternalLinks } from "./useInternalLinks";

type Props = {
  disabled?: boolean;
  modelValue: string | null;
  editorRef: any;
  onInit: (editor: any) => void;
};

type Emits = {
  "update:modelValue": [text: string];
  "update:editorRef": [r: any];
};

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
});
const emit = defineEmits<Emits>();

const model = useVModel(props, "modelValue", emit);
const editorRef = useVModel(props, "editorRef", emit);

const desktopToolbox = [
  "undo redo align | outdent indent bullist table link tableOfContents superscript",
  "bold italic underline | backcolor fontfamily fontsize blocks styles lineheight",
];

const mobileToolbox = [
  "undo redo align | bullist link tableOfContents",
  "bold italic underline | backcolor blocks",
];

const { language } = useNavigatorLanguage();
const { locale } = useI18n();
const openInternalLinks = useInternalLinks(() => props.disabled);
const scrollTinymceTocHeadingToView = useScrollTinymceTocHeadingToView();

const computedClass = computed(() => ({
  "!border-0 !caret-transparent !outline-none !ring-0": props.disabled,
}));

const spellcheckActive = computed(() =>
  language.value
    ? ["de", "de-DE", "de-AT", "de-CH", "de-LI", "de-LU"].includes(
        language.value
      )
    : false
);
const { isSmallerThanSm } = useLexMeaTailwindBreakpoints();

// force-rendering is required because vue is not re-rendering tinymce when resizing the viewport, and it will affect the toolbox.
const editorRenderKey = ref(0);
watch(isSmallerThanSm, () => editorRenderKey.value++);

watch(locale, () => {
  editorRenderKey.value++; // This forces TinyMCE to reinitialize with the new locale
});

const handleClick = (event) => {
  scrollTinymceTocHeadingToView(event);
  return openInternalLinks(event);
};
</script>
